import { SettingOutlined, ThunderboltOutlined } from "@ant-design/icons";
import { Divider, message, Modal, Space, Tabs, Typography } from "antd";
import { ModalWrapperProps } from "components/Modal/Modal";
import errorHandler from "errorHandler";
import { assign, isNil } from "lodash";
import {
  GetInfrasByPositionMeterIdQuery,
  NodeType,
  useGetInfrasByPositionMeterIdLazyQuery,
  usePositionConfigurationByPositionIdsQuery,
  useSwapChannelsMutation,
  useUpdateElectricalConfigurationMutation,
  useUpdateInfraNodeMutation,
} from "pacts/app-webcore/hasura-webcore.graphql";
import InfrastructureDetailsContext from "pages/InfrastructureDetails/InfrastructureDetails.Context";
import { MappedNodeDataType } from "pages/InfrastructureDetails/InfrastructureDetails.d";
import { InfrastructureType } from "pages/Infrastructures/InfrastructureDataType";
import React, { useContext, useEffect, useState } from "react";
import ElectricalConfigForm from "../../../../components/ElectricalConfig/ElectricalConfigForm";
import { ElectricalConfiguration, ElectricalConfigurationInput } from "../../../../types/electricalConfig";
import {
  getDefaultElectricalConfig,
  normalizePhaseType,
  normalizeSensorType,
  validateElectricalConfig,
} from "../../../../utils/electricalConfig";
import NodeInput from "../NodeInput/NodeInput";
import { transformToEditingNode, transformToUpdateNodeInput } from "./EditNodeModal.transformer";

const { Text } = Typography;

interface EditNodeModalProps {
  node: MappedNodeDataType;
  reloadInfrastructure: () => void;
}

const EditNodeModal = ({
  node,
  modalWrapperProps,
  closeModal,
  reloadInfrastructure,
}: EditNodeModalProps & ModalWrapperProps) => {
  const [nodeInput, setNodeInput] = useState<any>(transformToEditingNode(node));
  const [isInvalidInputData, setInvalidInputData] = useState<boolean>();
  const infraDetails = useContext(InfrastructureDetailsContext);
  const infraType = new InfrastructureType(infraDetails?.type);
  const [isInputChannelChanged, setIsInputChannelChanged] = useState<boolean>(false);
  const [infraChannels, setInfraChannels] = useState<GetInfrasByPositionMeterIdQuery["infrastructures"]>();
  const [activeTab, setActiveTab] = useState<string>("basic");
  const [electricalConfig, setElectricalConfig] = useState<ElectricalConfigurationInput>({});
  const [initialElectricalConfig, setInitialElectricalConfig] = useState<ElectricalConfiguration>({});

  const { id } = infraDetails ?? { id: "" };
  const [swapChannels] = useSwapChannelsMutation({
    onCompleted: () => {
      message.success("Swap channels successfully.");
    },
    onError: errorHandler.handleError,
  });

  const [getInfrasByPositionMeterId] = useGetInfrasByPositionMeterIdLazyQuery({
    variables: {
      meterPositionId: infraDetails?.meterPositionId,
    },
    onCompleted: (data) => {
      setInfraChannels(data.infrastructures);
    },
  });

  const [updateInfraNode, { loading: updateInfraNodeLoading }] = useUpdateInfraNodeMutation({
    onCompleted: () => {
      message.success("Edit node successful");
      closeModal();
      reloadInfrastructure();
    },
    onError: errorHandler.handleError,
  });

  const [updateElectricalConfig, { loading: updateElectricalConfigLoading }] = useUpdateElectricalConfigurationMutation(
    {
      onCompleted: () => {
        message.success("Electrical configuration updated successfully");
      },
      onError: (error) => {
        message.error(`Failed to update electrical configuration: ${error.message}`);
      },
    }
  );

  // Query position configuration for electrical settings from meter position
  const { data: positionConfigData } = usePositionConfigurationByPositionIdsQuery({
    variables: {
      positionIds: infraDetails?.meterPositionId ? [infraDetails.meterPositionId] : [],
    },
    skip: !infraDetails?.meterPositionId || node.nodeType !== NodeType.Energy,
  });

  const hasElectricalConfigChanged = (): boolean => {
    if (!initialElectricalConfig) return false;

    return JSON.stringify(electricalConfig) !== JSON.stringify(initialElectricalConfig);
  };

  const handleElectricalConfigUpdate = async () => {
    if (node.nodeType === NodeType.Energy && hasElectricalConfigChanged()) {
      // Filter out undefined, null, and empty string values but be more selective
      const cleanedElectricalConfig = Object.fromEntries(
        Object.entries(electricalConfig).filter(([, value]) => {
          // Keep the value if it's a valid number (including 0) or non-empty string
          if (typeof value === "number") {
            return !Number.isNaN(value) && Number.isFinite(value);
          }
          if (typeof value === "string") {
            return value.trim() !== "";
          }
          // For other types, exclude null/undefined
          return value !== undefined && value !== null;
        })
      );

      const validation = validateElectricalConfig(cleanedElectricalConfig);
      if (!validation.isValid) {
        message.error(`Electrical configuration validation failed: ${validation.errors.join(", ")}`);
        return;
      }

      // Use meter position ID instead of node position ID for electrical configuration
      const meterPositionId = infraDetails?.meterPositionId;
      if (!meterPositionId) {
        message.error("No meter position found for this energy node. Cannot update electrical configuration.");
        return;
      }

      try {
        await updateElectricalConfig({
          variables: {
            positionIds: [meterPositionId],
            electricalConfig: cleanedElectricalConfig,
          },
        });
      } catch (error: any) {
        message.error(`Error updating electrical configuration: ${error.message}`);
        // Don't block the main update if electrical config fails
      }
    }
  };

  const handleElectricalConfigChange = (changedValues: any, allValues: ElectricalConfigurationInput) => {
    setElectricalConfig(allValues);
  };

  const handleSubmit = async () => {
    try {
      // Handle node input changes first
      if (isInputChannelChanged && infraType.isCompressor() && infraDetails && infraChannels) {
        const destinationCompressor = infraChannels?.find((infra) => infra.phaseStreamIndex === nodeInput.mapInput);
        if (destinationCompressor) {
          Modal.confirm({
            title: "Warning",
            content: `This channel is already in use by ${destinationCompressor?.name}. If you wish to proceed the current channel used by this compressor will be swapped with the channel of the compressor using the desired channel right now.`,
            okText: "Proceed",
            onOk: async () => {
              await swapChannels({
                variables: {
                  startingCompressorId: id,
                  destinationCompressorId: destinationCompressor?.id,
                },
              });
              await updateInfraNode({
                variables: transformToUpdateNodeInput(infraDetails!.id, nodeInput) as any,
              });
              await handleElectricalConfigUpdate();
            },
          });
          return;
        }
      }

      // Update node input
      await updateInfraNode({
        variables: transformToUpdateNodeInput(infraDetails!.id, nodeInput) as any,
      });

      // Update electrical configuration if it's an energy node and config has changed
      await handleElectricalConfigUpdate();
    } catch (error: any) {
      message.error(`Error updating node: ${error.message}`);
    }
  };

  const handleInputChange = (
    inputChange: any,
    isInvalidData: boolean | undefined,
    isInputChanged: boolean | undefined
  ) => {
    setNodeInput(assign(nodeInput, inputChange));
    setInvalidInputData(isInvalidData);
    if (!isNil(isInputChanged)) {
      setIsInputChannelChanged(isInputChanged);
    }
  };

  useEffect(() => {
    if (infraDetails?.meterPositionId) {
      getInfrasByPositionMeterId();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [infraDetails]);

  // Extract complex expression for static checking
  const meterPositionId = infraDetails?.meterPositionId;

  // Load electrical configuration when position config data is available
  useEffect(() => {
    if (positionConfigData?.positionConfigurations?.[0] && node.nodeType === NodeType.Energy) {
      const config = positionConfigData.positionConfigurations[0];
      const electricalConfigData: ElectricalConfiguration = {
        voltage: config.voltage || undefined,
        frequency: config.frequency || undefined,
        powerFactor: config.powerFactor ? Number(config.powerFactor) : undefined,
        calibrationFactor: config.calibrationFactor ? Number(config.calibrationFactor) : undefined,
        nominalCurrent: config.nominalCurrent || undefined,
        phaseType: normalizePhaseType(config.phaseType),
        sensorType: normalizeSensorType(config.sensorType),
        primary: config.primary?.toString() || undefined,
        secondary: config.secondary?.toString() || undefined,
      };

      setInitialElectricalConfig(electricalConfigData);
      setElectricalConfig(electricalConfigData);
    } else if (node.nodeType === NodeType.Energy) {
      // Set default electrical configuration for energy nodes without position config
      const defaultConfig = getDefaultElectricalConfig();
      setInitialElectricalConfig(defaultConfig);
      setElectricalConfig(defaultConfig);
    }
  }, [positionConfigData, node.nodeType, node.nodeSubType, meterPositionId]);

  // Reset modal state when node changes
  useEffect(() => {
    setActiveTab("basic");
    setNodeInput(transformToEditingNode(node));
  }, [node]);

  const isEnergyNode = node.nodeType === NodeType.Energy;
  const isEnergyAdapterModbusSubtype = node.nodeSubType === "energy_adapter_modbus";
  const shouldShowAdvanced = isEnergyAdapterModbusSubtype && node.nodeSubType !== null;
  const isLoading = updateInfraNodeLoading || updateElectricalConfigLoading;

  return (
    <Modal
      title="Edit Node"
      centered
      onOk={handleSubmit}
      width={isEnergyNode ? 600 : 450}
      okText="Confirm"
      okButtonProps={{
        disabled: isInvalidInputData,
      }}
      confirmLoading={isLoading}
      {...modalWrapperProps}
    >
      {isEnergyNode ? (
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={[
            {
              key: "basic",
              label: (
                <Space>
                  <ThunderboltOutlined />
                  <span>Basic Settings</span>
                </Space>
              ),
              children: (
                <div style={{ minHeight: 200 }}>
                  <NodeInput type={infraType} node={nodeInput} handleInputChange={handleInputChange} />
                </div>
              ),
            },
            {
              key: "electrical",
              label: (
                <Space>
                  <SettingOutlined />
                  <span>Electrical Configuration</span>
                </Space>
              ),
              children: (
                <div style={{ minHeight: 200, padding: "16px 0" }}>
                  <Divider>
                    <Space>
                      <SettingOutlined />
                      <Text>Configure Electrical Parameters</Text>
                    </Space>
                  </Divider>
                  <ElectricalConfigForm
                    initialValues={electricalConfig}
                    onValuesChange={handleElectricalConfigChange}
                    showAdvanced={shouldShowAdvanced}
                    compact={false}
                    editMode
                  />
                </div>
              ),
            },
          ]}
        />
      ) : (
        <NodeInput type={infraType} node={nodeInput} handleInputChange={handleInputChange} />
      )}
    </Modal>
  );
};

export default EditNodeModal;
